import axios from 'axios';
import apiConfig from '../../config/apiConfig.js';

// Gift Item interfaces
export interface GiftItem {
  _id: string;
  title: string;
  type: 'Monetary' | 'GiftCard' | 'PhysicalItem';
  category?: string;
  description?: string;
  imageUrl?: string;
  price?: number;
  denominations?: number[]; // For gift cards
  createdAt: string;
  updatedAt: string;
  deleted: boolean;
}

// API Request interfaces
export interface GetGiftItemsParams {
  search?: string;
  type?: string;
  page?: number;
  limit?: number;
  category?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  priceMin?: number;
  priceMax?: number;
}

export interface AddGiftItemRequest {
  type: 'Monetary' | 'GiftCard' | 'PhysicalItem';
  giftItemId: string;
  amount?: number;
  selectedAmount?: number;
  sendMethod?: 'Email' | 'Postal';
}

export interface UpdateGiftContactRequest {
  recipientSnapshot?: {
    name: string;
    email?: string;
    relationship: string;
  };
  occasion?: string;
  date?: string;
  deliverySchedule?: string;
  status?: string;
  primaryLegacyContact?: {
    name: string;
    phoneNumber: string;
    email: string;
    address: string;
    relationship: string;
  };
}

// API Response interfaces
export interface GiftItemResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: GiftItem | GiftItem[];
  pagination?: {
    totalCount: number;
    totalPages: number;
    currentPage: number;
  };
}

export interface GiftResponse {
  status: 'Success' | 'Failed';
  message: string;
  data?: any; // This will be the updated gift object
}

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` })
  };
};

export const giftItemApi = {
  // Get all gift items with pagination, search, and filtering
  getAllGiftItems: async (params: GetGiftItemsParams = {}): Promise<GiftItemResponse> => {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.search) queryParams.append('search', params.search);
      if (params.type) queryParams.append('type', params.type);
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.category) queryParams.append('category', params.category);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.priceMin !== undefined) queryParams.append('priceMin', params.priceMin.toString());
      if (params.priceMax !== undefined) queryParams.append('priceMax', params.priceMax.toString());

      const response = await axios.get(
        `${apiConfig.apiBaseURL}/gift/get-all-gift-items?${queryParams.toString()}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching gift items:', error);
      return {
        status: 'Failed',
        message: error.response?.data?.message || 'Failed to fetch gift items',
        data: [],
        pagination: {
          totalCount: 0,
          totalPages: 0,
          currentPage: 1,
        },
      };
    }
  },

  // Get single gift item by ID
  getSingleGiftItem: async (giftItemId: string): Promise<GiftItemResponse> => {
    try {
      const response = await axios.get(
        `${apiConfig.apiBaseURL}/gift/get-single-gift-item/${giftItemId}`,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error fetching gift item:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch gift item');
    }
  },

  // Add gift item to a gift
  addGiftItemToGift: async (giftId: string, giftItemData: AddGiftItemRequest): Promise<GiftResponse> => {
    try {
      const response = await axios.post(
        `${apiConfig.apiBaseURL}/gift/add-gift-item-to-gift/${giftId}`,
        giftItemData,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error adding gift item to gift:', error);
      throw new Error(error.response?.data?.message || 'Failed to add gift item to gift');
    }
  },

  // Update gift item in a gift
  updateGiftItemInGift: async (giftId: string, giftItemData: AddGiftItemRequest): Promise<GiftResponse> => {
    try {
      const response = await axios.put(
        `${apiConfig.apiBaseURL}/gift/update-gift-item-in-gift/${giftId}`,
        giftItemData,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error updating gift item in gift:', error);
      throw new Error(error.response?.data?.message || 'Failed to update gift item in gift');
    }
  },

  // Remove gift item from a gift
  removeGiftItemFromGift: async (giftId: string): Promise<GiftResponse> => {
    try {
      const response = await axios.delete(
        `${apiConfig.apiBaseURL}/gift/remove-gift-item/${giftId}`,
        {
          headers: getAuthHeaders()
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error removing gift item from gift:', error);
      throw new Error(error.response?.data?.message || 'Failed to remove gift item from gift');
    }
  },

  // Get gift items by category (helper function)
  getGiftItemsByCategory: async (category: string, type?: string): Promise<GiftItemResponse> => {
    return giftItemApi.getAllGiftItems({
      category,
      type,
      limit: 100 // Get all items in category
    });
  },

  // Search gift items (helper function)
  searchGiftItems: async (searchTerm: string, type?: string): Promise<GiftItemResponse> => {
    return giftItemApi.getAllGiftItems({
      search: searchTerm,
      type,
      limit: 20
    });
  },

  // Get gift items by price range (helper function)
  getGiftItemsByPriceRange: async (priceMin: number, priceMax: number, type?: string): Promise<GiftItemResponse> => {
    return giftItemApi.getAllGiftItems({
      type,
      priceMin,
      priceMax,
      sortBy: 'price',
      sortOrder: 'asc'
    });
  },

  // Update gift contact information
  updateGiftContact: async (giftId: string, contactData: UpdateGiftContactRequest): Promise<GiftResponse> => {
    try {
      const response = await axios.put(
        `${apiConfig.apiBaseURL}/gift/update-gift-contact/${giftId}`,
        contactData,
        {
          headers: getAuthHeaders()
        }
      );
      
      return response.data;
    } catch (error: any) {
      console.error('Error updating gift contact:', error);
      throw new Error(error.response?.data?.message || 'Failed to update gift contact');
    }
  }
};